/**
 * Firebase App Check - protect Firestore, Storage, and Auth from abuse.
 * Uses reCAPTCHA v3 (invisible). Enable enforcement in Firebase Console after testing.
 *
 * Setup: Firebase Console > App Check > Register app > reCAPTCHA v3, add site key.
 * Add REACT_APP_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY to .env for the reCAPTCHA v3 site key.
 */

import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { Firebase } from './config';

let appCheckInitialized = false;

/**
 * Initialize App Check. Call once after Firebase app init (e.g. in config or App.js).
 * If no site key is set, App Check is skipped (e.g. in dev without .env).
 *
 * TEMPORARY DISABLE FOR TESTING:
 * Set REACT_APP_DISABLE_APP_CHECK=true in .env to temporarily disable App Check.
 * This is useful for debugging authentication issues without App Check interference.
 *
 * App Check can block registration requests if enforcement is enabled in Firebase Console.
 * Common error codes when App Check blocks requests:
 * - 400: Invalid request (often due to missing or invalid App Check token)
 *
 * To disable App Check temporarily for testing:
 * 1. Add REACT_APP_DISABLE_APP_CHECK=true to your .env file
 * 2. Restart the development server
 * 3. Test registration without App Check
 * 4. Remove the flag after debugging to re-enable protection
 */
export function initAppCheck() {
  if (appCheckInitialized) return true;
  
  // ВРЕМЕННОЕ ОТКЛЮЧЕНИЕ APP CHECK ДЛЯ ТЕСТИРОВАНИЯ
  // Установите REACT_APP_DISABLE_APP_CHECK=true в .env для отключения
  const disableAppCheck = process.env.REACT_APP_DISABLE_APP_CHECK === 'true';
  if (disableAppCheck) {
    console.warn('[APP CHECK] TEMPORARILY DISABLED for testing. Set REACT_APP_DISABLE_APP_CHECK=false or remove from .env to re-enable.');
    return false;
  }
  
  const siteKey = process.env.REACT_APP_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[APP CHECK] Skipped: REACT_APP_FIREBASE_APP_CHECK_RECAPTCHA_SITE_KEY not set');
    }
    return false;
  }
  try {
    initializeAppCheck(Firebase, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    appCheckInitialized = true;
    console.info('[APP CHECK] Initialized successfully with reCAPTCHA v3');
    return true;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[APP CHECK] Init failed:', e.message);
    }
    return false;
  }
}
